import { Sparkles, Users } from 'lucide-react'
import { RevealCurtain, useRevealCountdown } from './RevealCountdown'
import { CEREMONY_SUBTITLE, EVENT_FOOTER, StageChrome, type StagePalette } from './StageChrome'
import { cn } from '../../lib/cn'
import { stageSound } from '../../lib/stageAudio'
import type { AwardView } from '../admin/screenTypes'

/**
 * Most Active Club — typed by Admin, announced on its own stage.
 *
 * The fifth stage in the ceremony, and it has to look like none of the other
 * four. The two booth awards share a podium, Best Traditional Attire has its
 * twin portraits, and Most Active School types its winner out over a rolling
 * count. This one opens: a crest expands out of the centre with a ring
 * sweeping round it, and the clubs rise into place one after another.
 *
 * There is no podium here because there is no ranking — the clubs share the
 * award, so every card is drawn identically. They are announced one at a time
 * so the compère can name each in turn, and each stays on screen as the next
 * arrives: by the end the hall sees all six together, which is what makes
 * them read as one shared award rather than six separate winners.
 *
 * All of it is CSS — no canvas, no library. The projector laptop is also
 * running the console and an SSE stream, and the one thing this must not do
 * is drop frames during the announcement.
 */

/** Indigo and violet: the one palette not yet used by an award. */
const PALETTE: StagePalette = {
  background:
    'radial-gradient(ellipse at 50% 38%, rgba(196,181,253,0.20), transparent 58%), repeating-conic-gradient(from 206deg at 50% 40%, rgba(255,255,255,0.05) 0deg 6deg, transparent 6deg 17deg), linear-gradient(130deg, #2C1A6B 0%, #4B2E9E 44%, #1B1142 100%)',
  border: '#140B33',
  sparkle: '#E9D5FF',
  sparkleAlt: '#A78BFA',
}

export function ClubScreen({ award }: { award: AwardView }) {
  /*
   * Every club called so far, in the order they were read out.
   *
   * The clubs are announced one at a time but they **stay on the screen** —
   * by the end the hall is looking at all six together, which is what makes
   * them read as sharing the award rather than as six separate winners.
   */
  const called = award.results.filter((r) => r.revealed).sort((a, b) => a.place - b.place)
  const names = called.map((r) => r.club?.names[0] ?? '')

  /** The one just announced — the count belongs to whichever opened last. */
  const newest = called
    .filter((r) => r.revealedAt)
    .sort((a, b) => (b.revealedAt! > a.revealedAt! ? 1 : -1))[0]

  const { pending, count } = useRevealCountdown(
    newest ? `club:${newest.place}:${newest.revealedAt}` : null,
    'purple',
    newest?.revealedAt ?? null,
    /* The same award fanfare every other reveal gets. */
    () => stageSound.awardReveal(),
  )

  const counting = pending != null
  /*
   * The crest opens on the first club and stays open. `counting` hides only
   * the club currently behind the curtain — the ones already announced must
   * not vanish and reappear each time the next one is called.
   */
  const open = called.length > 0 && !(counting && called.length === 1)
  /** How many to show: everything except the one still behind the curtain. */
  const shown = counting ? names.slice(0, -1) : names
  const revealed = called.length > 0

  return (
    <StageChrome
      palette={PALETTE}
      title={award.title}
      subtitle={CEREMONY_SUBTITLE}
      status={
        award.complete
          ? 'All clubs announced'
          : revealed
            ? `Announced ${shown.length} of ${award.places}`
            : 'Awaiting announcement'
      }
      footerLeft={EVENT_FOOTER}
    >
      <div className="flex w-full flex-col items-center px-[4vmin] text-center">
        {!open ? (
          /*
           * Held shut until the curtain lifts, not merely until the server
           * reveals it — those are different moments, and the curtain is
           * only 85% opaque. Rendering the winners underneath it would leave
           * the club names faintly legible on the projector for the ten
           * seconds they are supposed to be a secret.
           *
           * Before the reveal there is nothing here to hide anyway: the
           * server withholds the names from the payload entirely.
           */
          <>
            <span className="grid size-[14vmin] place-items-center rounded-full border-[0.4vmin] border-dashed border-white/25">
              <Users className="size-[6vmin] text-white/35" strokeWidth={1.6} />
            </span>
            <p className="mt-[3vmin] text-[3vmin] font-bold uppercase tracking-[0.3em] text-white/50">
              {counting ? 'Announcing now' : 'The clubs of the year'}
            </p>
          </>
        ) : (
          <>
            {/*
              The crest. Two rings and a disc: the outer ring sweeps round
              once as it appears, which is what makes it read as opening
              rather than simply being there.
            */}
            <div className={cn('relative grid size-[20vmin] place-items-center', open && 'club-crest')}>
              <span
                className={cn(
                  'absolute inset-0 rounded-full border-[0.5vmin] border-[#E9D5FF]/35',
                  open && 'club-ring',
                )}
              />
              <span className="absolute inset-[1.6vmin] rounded-full border-[0.25vmin] border-[#E9D5FF]/25" />
              <span className="grid size-[12vmin] place-items-center rounded-full bg-linear-to-b from-[#C4B5FD] to-[#8B5CF6] shadow-[0_8px_34px_rgba(167,139,250,0.45)]">
                <Users className="size-[6vmin] text-[#2A1364]" strokeWidth={2.2} />
              </span>
            </div>

            <span
              className={cn(
                'mt-[2.4vmin] inline-flex items-center gap-[1.2vmin] rounded-full bg-[#E9D5FF] px-[3vmin] py-[1vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.18em] text-[#3B1E7A] shadow-[0_5px_22px_rgba(233,213,255,0.3)]',
                open && 'club-rise club-delay-1',
              )}
            >
              <Sparkles className="size-[2.2vmin]" strokeWidth={2.4} />
              {shown.length > 1 ? 'Most active clubs' : 'Most active club'}
            </span>

            {/*
              The clubs called so far, with no number against any of them —
              they share the award, so the grid keeps them visibly equal: same
              size, same weight, same box. A rank would be wrong here, so
              there is not even an implied one.

              Two columns once there are more than three, because six names
              down a single column pushes the type below what reads at 3
              metres. Each card rises as it is announced; the ones already up
              do not re-animate, because their key and classes do not change.
            */}
            <div
              className={cn(
                'mt-[3vmin] grid w-full max-w-[86vw] gap-[1.6vmin]',
                shown.length > 3 ? 'grid-cols-2' : 'grid-cols-1',
              )}
            >
              {shown.map((name, i) => (
                <div
                  key={name}
                  className={cn(
                    'flex items-center justify-center rounded-[1.4vmin] border-[0.25vmin] border-[#E9D5FF]/30 bg-white/10 px-[2vmin] py-[1.6vmin]',
                    'club-rise',
                  )}
                >
                  <span className="truncate text-[3.4vmin] font-extrabold leading-tight text-white drop-shadow-[0_3px_14px_rgba(14,6,40,0.5)]">
                    {name}
                  </span>
                </div>
              ))}
            </div>

            {/* Only once the last club has been called — not after each one. */}
            {award.complete && !counting && (
              <p className="club-rise club-delay-1 mt-[3vmin] text-[3.2vmin] font-bold italic text-[#E9D5FF]">
                Congratulations!
              </p>
            )}
          </>
        )}
      </div>

      {counting && <RevealCurtain title={award.title} place="The winners" count={count} />}
    </StageChrome>
  )
}
